import { useState } from 'react';
import { useResponseActions, executeResponseAction } from '../../hooks/useSecOps';

interface ResponseAction {
  id: string;
  action_type: string;
  target: string;
  incident_id?: string;
  incident_title?: string;
  status: string;
  priority: string;
  executed_by: string;
  executed_at: string;
  result?: string;
}

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-600',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  rolled_back: 'bg-orange-100 text-orange-700',
};

const PRIORITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-blue-100 text-blue-700',
};

const ACTION_TYPES = [
  'isolate_host', 'block_ip', 'block_domain', 'disable_account', 'reset_password',
  'quarantine_file', 'kill_process', 'update_firewall_rule', 'revoke_token', 'custom',
];

function Badge({ value, colors }: { value: string; colors: Record<string, string> }) {
  const label = value.replace(/_/g, ' ');
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${colors[value] ?? 'bg-gray-100 text-gray-600'}`}>{label}</span>;
}

export default function ResponseActionsPage() {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [showExecute, setShowExecute] = useState(false);
  const [form, setForm] = useState({ action_type: 'block_ip', target: '', priority: 'medium', incident_id: '', notes: '' });
  const [executing, setExecuting] = useState(false);

  const { data, loading, error, refresh } = useResponseActions(filters);
  const actions = (data?.actions ?? []) as ResponseAction[];

  function setFilter(key: string, value: string) {
    setFilters(f => value ? { ...f, [key]: value } : Object.fromEntries(Object.entries(f).filter(([k]) => k !== key)));
  }

  async function handleExecute() {
    if (!form.target) return;
    setExecuting(true);
    try {
      await executeResponseAction(form);
      setShowExecute(false);
      setForm({ action_type: 'block_ip', target: '', priority: 'medium', incident_id: '', notes: '' });
      refresh();
    } catch { /* handled */ } finally {
      setExecuting(false);
    }
  }

  if (loading) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="h-96 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Response Actions</h1>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">{data?.total ?? 0} total</span>
          <button onClick={() => setShowExecute(true)} className="px-3 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700">
            Execute Action
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm flex items-center justify-between">{error} <button onClick={refresh} className="text-sm font-medium underline">Retry</button></div>}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 flex flex-wrap gap-3 items-center">
        <select value={filters.action_type ?? ''} onChange={e => setFilter('action_type', e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm">
          <option value="">All Types</option>
          {ACTION_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}</option>)}
        </select>

        <select value={filters.status ?? ''} onChange={e => setFilter('status', e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm">
          <option value="">All Statuses</option>
          {['pending', 'in_progress', 'completed', 'failed', 'rolled_back'].map(s =>
            <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}</option>
          )}
        </select>

        {Object.keys(filters).length > 0 && (
          <button onClick={() => setFilters({})} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">Clear filters</button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-6 py-3">Action Type</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Target</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Incident</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Status</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Priority</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Executed By</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Timestamp</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {actions.length === 0 && (
              <tr><td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-400">No response actions found.</td></tr>
            )}
            {actions.map(action => (
              <tr key={action.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <p className="text-sm font-medium text-gray-900 capitalize">{action.action_type.replace(/_/g, ' ')}</p>
                </td>
                <td className="px-4 py-4 text-sm text-gray-700 font-mono">{action.target}</td>
                <td className="px-4 py-4 text-sm text-gray-700">{action.incident_title || action.incident_id || '-'}</td>
                <td className="px-4 py-4"><Badge value={action.status} colors={STATUS_COLORS} /></td>
                <td className="px-4 py-4"><Badge value={action.priority} colors={PRIORITY_COLORS} /></td>
                <td className="px-4 py-4 text-sm text-gray-700">{action.executed_by || '-'}</td>
                <td className="px-4 py-4 text-xs text-gray-500">{action.executed_at ? new Date(action.executed_at).toLocaleString() : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Execute Modal */}
      {showExecute && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowExecute(false)}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Execute Response Action</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Action Type</label>
                <select value={form.action_type} onChange={e => setForm(f => ({ ...f, action_type: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                  {ACTION_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target *</label>
                <input type="text" value={form.target} onChange={e => setForm(f => ({ ...f, target: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="IP, hostname, account, file hash..." />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                    {['critical', 'high', 'medium', 'low'].map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Incident ID</label>
                  <input type="text" value={form.incident_id} onChange={e => setForm(f => ({ ...f, incident_id: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="Optional..." />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  rows={2} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="Additional context..." />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowExecute(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button onClick={handleExecute} disabled={executing || !form.target}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50">
                {executing ? 'Executing...' : 'Execute'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
